import axios from "axios";
import { URL, SUCCESS_RESULT } from "../Constants";
import {
  getEncryptedAccessToken,
  getEncryptedRefreshToken,
  setEncryptedAccessToken,
  setEncryptedRefreshToken,
  setTokenExpiresAt,
  setTokenExpiresAtFromJwt,
  getTokenExpiresAt,
  saveRedirectUrlAndGoToLogin,
} from "./Utils";

const REFRESH_ENDPOINT = `${URL}refresh-token`;
const REFRESH_LEAD_MS = 60_000;

let isRefreshing = false;
let refreshQueue = [];

const processQueue = (error, token = null) => {
  refreshQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  refreshQueue = [];
};

const applyRefreshPayload = (payload) => {
  const newToken = payload?.token;
  if (!newToken) {
    throw new Error("Refresh response missing token");
  }
  setEncryptedAccessToken(newToken);
  if (payload.refresh_token) {
    setEncryptedRefreshToken(payload.refresh_token);
  }
  if (payload.expires_in != null) {
    setTokenExpiresAt(payload.expires_in);
  } else {
    setTokenExpiresAtFromJwt(newToken);
  }
  return newToken;
};

export const performTokenRefresh = async () => {
  const refreshToken = getEncryptedRefreshToken();
  if (!refreshToken) {
    throw new Error("No refresh token");
  }

  const { data } = await axios.post(
    REFRESH_ENDPOINT,
    { refresh_token: refreshToken },
    { skipAuthRefresh: true }
  );

  if (data?.status != null && data.status !== SUCCESS_RESULT) {
    throw new Error(data?.message || "Refresh failed");
  }

  const payload = data?.data || data;
  return applyRefreshPayload(payload);
};

const shouldRefreshToken = () => {
  const refreshToken = getEncryptedRefreshToken();
  if (!refreshToken) {
    return false;
  }

  let expiresAt = getTokenExpiresAt();
  if (!expiresAt) {
    const accessToken = getEncryptedAccessToken();
    if (accessToken) {
      setTokenExpiresAtFromJwt(accessToken);
      expiresAt = getTokenExpiresAt();
    }
  }

  return Boolean(expiresAt && Date.now() > expiresAt - REFRESH_LEAD_MS);
};

export const refreshAccessTokenWithQueue = async () => {
  if (!isRefreshing) {
    isRefreshing = true;
    try {
      const newToken = await performTokenRefresh();
      processQueue(null, newToken);
      return newToken;
    } catch (error) {
      processQueue(error, null);
      throw error;
    } finally {
      isRefreshing = false;
    }
  }

  return new Promise((resolve, reject) => {
    refreshQueue.push({ resolve, reject });
  });
};

export const ensureValidAccessToken = async () => {
  if (!shouldRefreshToken()) {
    return getEncryptedAccessToken();
  }
  return refreshAccessTokenWithQueue();
};

export const createAuthRequestInterceptor = () => async (config) => {
  if (config.skipAuthRefresh) {
    const token = getEncryptedAccessToken();
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  }

  try {
    const token = await ensureValidAccessToken();
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
  } catch {
    saveRedirectUrlAndGoToLogin();
    return Promise.reject(new Error("Session expired"));
  }

  return config;
};

export const createAuthResponseInterceptor = (axiosInstance) => async (error) => {
  const original = error.config;

  if (
    !original ||
    original.skipAuthRefresh ||
    original._retry ||
    error.response?.status !== 401
  ) {
    return Promise.reject(error);
  }

  const refreshToken = getEncryptedRefreshToken();
  if (!refreshToken) {
    saveRedirectUrlAndGoToLogin();
    return Promise.reject(error);
  }

  original._retry = true;

  try {
    const newToken = await refreshAccessTokenWithQueue();
    original.headers = original.headers || {};
    original.headers.Authorization = `Bearer ${newToken}`;
    return axiosInstance(original);
  } catch {
    saveRedirectUrlAndGoToLogin();
    return Promise.reject(error);
  }
};
